import { Box, BoxProps, useId, useTheme } from "@chakra-ui/react";
import { css, Global } from "@emotion/react";
//@ts-ignore
import Mazemap from "@itpi/mazemap";
import { MarkerOptions } from "mapbox-gl";
import { useContext, useEffect } from "react";

import { MapContext } from "../context/map.context";

import "@itpi/mazemap/mazemap.min.css";

export type MapPin = MarkerOptions & {
  lat: number;
  lng: number;
  zLevel?: number;
  onClick?: () => void;
  glyph?: string;
  shape?: string;
  size?: number;
  radius?: number;
};

export const defaultLocation = {
  pitch: 0,
  bearing: 0,
  zoom: 14.5,
  center: {
    lng: -2.785908,
    lat: 54.008355,
  },
};

const Map = (boxProps: BoxProps) => {
  const mapId = useId();
  const { map, setMap, pins , setFloorLevel} = useContext(MapContext);
  const theme = useTheme();

  // @ts-ignore
  window.Mazemap = Mazemap;

  useEffect(() => {
    const mazemap = new Mazemap.Map({
      container: mapId,
      campuses: 341,
      zLevelControl: false,
      ...defaultLocation,
    });

    mazemap.on("load", () => {
      const floorBar = new Mazemap.ZLevelBarControl();
      mazemap.addControl(floorBar, "top-left");
    });


    setMap(mazemap);
  }, [mapId, setMap]);

  // Sets the floor level in a state when clicking on the left menu
  useEffect(() => {
    if (!map) return;

    const onZLabelClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && target.classList.contains("z-label")) {
        setFloorLevel(map.zLevel);
      }
    };

    document.addEventListener("click", onZLabelClick);

    return () => {
      document.removeEventListener("click", onZLabelClick);
    };
  }, [map, setFloorLevel]);

  useEffect(() => {
    if (map) {
      map.on("load", () => {
        if (pins) {
          const markers = pins.map((pin) => {
            const { lat, lng, onClick, radius, ...config } = pin;

            // Add the marker
            const marker = new Mazemap.MazeMarker({
              color: theme.colors?.["lancsRed"]?.["300"],
              ...config,
            })
              .setLngLat({ lat, lng })
              .addTo(map);

            // Attach click event to marker
            marker.on("click", onClick);

            return { marker };
          });

          // Cleanup markers and layers on unmount
          return () => {
            markers.forEach(({ marker }) => {
              marker.remove();
            });
            pins.forEach(({ lat, lng }) => {
              if (map.getSource(`circle-${lng}-${lat}`)) {
                map.removeLayer(`circle-layer-${lng}-${lat}`);
                map.removeSource(`circle-${lng}-${lat}`);
              }
            });
          };
        }
      });
    }

  }, [pins, map, theme.colors]);


  return (
    <>
      <Global
        styles={css`
          .mapboxgl-ctrl-bottom-left {
            z-index: 100;
          }

          .mazemap-control-zlevel-bar {
            color: black;
          }
        `}
      />
      <Box id={mapId} w="full" h="full" {...boxProps} />
    </>
  );
};

export default Map;
